import { createAction } from 'typesafe-actions';
import { takeLatest, put, call } from 'redux-saga/effects';
import { PDP_REVIEWS_ADD_NEW_CLICK } from 'features/product-reviews';
import { Actions } from 'features/review-form/state';
import log from 'loglevel';
import { CallEffectReturnType } from 'types/utils';
import { ReviewFormActions } from 'features/review-form';
import { EReviewsElementBlock } from './types';

type Score = '1' | '2' | '3' | '4' | '5';
type DurationOfUseIndex = '1' | '2' | '3' | '4';

export namespace ReviewsGaClickButton {
  const ACTION_NAME = 'GA:clickButton';

  /**
   * Событие должно срабатывать при клике на кнопку Написать отзыв
   *
   * @see https://docs.google.com/document/d/1vAT_kS09tM_69q5YshNF9o88nh3O1n17Tmt2nKdd8gw/edit#heading=h.uv70gc68awpo
   */
  export const addNewReview = createAction(
    ACTION_NAME,
    () =>
      ({
        elementType: 'button',
        elementBlock: EReviewsElementBlock.PAGE_SECTION, // блок, в котором расположен элемент

        elementName: 'WriteReview', // условное обозначение кнопки
        elementText: 'Написать отзыв', // текст на кнопке
      }) as const,
  )();

  /**
   * @see https://docs.google.com/document/d/14v6VhSpFLlEPekvGSRJh5klKkGJp6oPhplFlxorrAyM/edit#heading=h.6iyxi97hhg11
   */
  export const rate = createAction(
    ACTION_NAME,
    ({ elementName, elementText }: { elementName: Score; elementText: string }) => ({
      elementBlock: EReviewsElementBlock.FORM,
      elementName,
      elementText,
    }),
  )();

  /**
   * @see https://docs.google.com/document/d/14v6VhSpFLlEPekvGSRJh5klKkGJp6oPhplFlxorrAyM/edit#heading=h.ip4eyd889v1i
   */
  export const recommend = createAction(ACTION_NAME, ({ elementName }: { elementName: 'yes' | 'no' }) => ({
    elementBlock: EReviewsElementBlock.FORM,
    elementName,
    elementText: 'Рекомендуете этот товар?',
  }))();

  /**
   * @see https://docs.google.com/document/d/14v6VhSpFLlEPekvGSRJh5klKkGJp6oPhplFlxorrAyM/edit#heading=h.o2qkhh56xye1
   */
  export const selectPhoto = createAction(ACTION_NAME, () => ({
    elementType: 'button',
    elementBlock: EReviewsElementBlock.FORM,
    elementName: 'photo',
    elementText: 'Загрузка фото',
  }))();

  /**
   * @see https://docs.google.com/document/d/14v6VhSpFLlEPekvGSRJh5klKkGJp6oPhplFlxorrAyM/edit#heading=h.sfb1lvrrqpw
   */
  export const setDurationOfUse = createAction(
    ACTION_NAME,
    ({ elementName }: { elementName: DurationOfUseIndex }) => ({
      elementType: 'button',
      elementBlock: EReviewsElementBlock.FORM,
      elementName,
      elementText: 'Срок использования',
    }),
  )();

  /**
   * @see https://docs.google.com/document/d/14v6VhSpFLlEPekvGSRJh5klKkGJp6oPhplFlxorrAyM/edit#heading=h.epkse0p5nz6q
   */
  export const setRegion = createAction(ACTION_NAME, ({ elementName }: { elementName: string }) => ({
    elementType: 'button',
    elementBlock: EReviewsElementBlock.FORM,
    elementName,
    elementText: 'Место покупки - город',
  }))();

  /**
   * @see https://docs.google.com/document/d/14v6VhSpFLlEPekvGSRJh5klKkGJp6oPhplFlxorrAyM/edit#heading=h.epkse0p5nz6q
   */
  export const setShop = createAction(ACTION_NAME, ({ elementName }: { elementName: string }) => ({
    elementType: 'button',
    elementBlock: EReviewsElementBlock.FORM,
    elementName,
    elementText: 'Место покупки - магазин',
  }))();
}

export function* runGaClickButtonDetector() {
  yield takeLatest(PDP_REVIEWS_ADD_NEW_CLICK, function* putGaClickButton() {
    yield put(ReviewsGaClickButton.addNewReview());
  });

  yield takeLatest(Actions.setTotalScore, function* putGaClickButton({ payload }) {
    yield call(putRateActionIfValid, { score: payload, elementText: 'Общая оценка' });
  });

  yield takeLatest(ReviewFormActions.PHOTO_SELECTION_CLICK, function* putGaClickButton() {
    yield put(ReviewsGaClickButton.selectPhoto());
  });

  yield takeLatest(Actions.setAuthorDetailTimeUsage, function* putGaClickButton({ payload }) {
    yield put(
      ReviewsGaClickButton.setDurationOfUse({
        elementName: String(payload.indexFromOne) as DurationOfUseIndex,
      }),
    );
  });

  yield takeLatest(ReviewFormActions.AUTHOR_REGION_SELECTED, function* putGaClickButton({ payload }) {
    yield put(ReviewsGaClickButton.setRegion({ elementName: payload.regionName }));
  });

  yield takeLatest(ReviewFormActions.AUTHOR_SHOP_SELECTED, function* putGaClickButton({ payload }) {
    yield put(ReviewsGaClickButton.setShop({ elementName: payload.shopName }));
  });

  function* putRateActionIfValid({ score, elementText }: { score: number; elementText: string }) {
    const intScore = (yield call(toInt, score)) as CallEffectReturnType<typeof toInt>;

    if (isIntScoreValid(intScore)) {
      yield put(
        ReviewsGaClickButton.rate({
          elementName: String(intScore) as Score,
          elementText,
        }),
      );
    } else {
      yield call([log, 'warn'], `Invalid score for '${elementText}' element`);
    }
  }

  function toInt(value: number) {
    return Math.round(value);
  }

  function isIntScoreValid(score: number) {
    return score >= 1 && score <= 5;
  }
}
